import * as React from 'react';
import {useSelector, useDispatch} from 'react-redux';

import type {FilterObject} from 'nutshell-core/types';

import type {NutshellState} from '../../../store';

import {makeHandleClickChartToZoom} from '../charts/make-handle-click-chart-to-zoom';
import {LeadsAndActivitiesReportProvider} from '../leads-and-activities-report-provider';
import {ReportChart} from '../report-chart';
import {GAP_SELECT_VALUES_DAY_TO_YEAR, GAP_SELECT_INDEX_DAY} from '../report-constants';
import {QUOTES, INVOICES} from '../report-types';
import type {ReportKey, ChartReportParams} from '../report-types';
import {requestLeadsReportData} from '../reports-actions';
import * as reportSelectors from '../reports-selectors';

type Props = {
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    activeFilters: FilterObject[];
    reportParams: any;
    salesDocumentType: 'quote' | 'invoice';
};

export function QuotesReportChart(props: Props) {
    const reportKey = props.salesDocumentType === 'quote' ? QUOTES : INVOICES;

    const chartBucketCount = useSelector((state: NutshellState) =>
        reportSelectors.getCurrentReportChartBucketCount(state)
    );
    const dispatch = useDispatch();

    const fetchReportData = React.useCallback(
        (reportKeyForFetch: ReportKey, reportParams: ChartReportParams, filters: Array<any>) => {
            dispatch(requestLeadsReportData({reportKey: reportKeyForFetch, reportParams, filters}));
        },
        [dispatch]
    );

    const activeFiltersString = JSON.stringify(props.activeFilters);
    const reportParamsString = JSON.stringify(props.reportParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedActiveFilters = React.useMemo(() => props.activeFilters, [activeFiltersString]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedReportParams = React.useMemo(() => props.reportParams, [reportParamsString]);

    React.useEffect(() => {
        fetchReportData(reportKey, memoizedReportParams, memoizedActiveFilters);
    }, [fetchReportData, memoizedReportParams, memoizedActiveFilters, reportKey]);

    let currentGapIndex = GAP_SELECT_VALUES_DAY_TO_YEAR.findIndex((gap) => {
        return gap.id === props.reportParams.gap;
    });
    if (currentGapIndex === -1) {
        currentGapIndex = GAP_SELECT_INDEX_DAY;
    }

    return (
        <LeadsAndActivitiesReportProvider>
            <ReportChart
                activeFilters={memoizedActiveFilters}
                // @ts-expect-error
                onChartClick={makeHandleClickChartToZoom(
                    currentGapIndex,
                    chartBucketCount,
                    props.onParamPush,
                    props.reportParams.dateRangeType
                )}
                onParamChange={props.onParamChange}
                reportKey={reportKey}
                reportParams={memoizedReportParams}
                fitHeight={true}
                isFullWidth={true}
            />
        </LeadsAndActivitiesReportProvider>
    );
}
